import * as THREE from "three";
import { Line, Vector3 } from "opengeometry";
import { Glyphs } from "@opengeometry/openglyph";

import { ElementType } from "../elements/base-type";
import { OPElement, IPlaceable } from "../elements/op-element";
import { IShape } from "../shapes/base-type";
import { Event } from "../../../../utils/event";
import { Y_UP, degToRad, rotatePoint } from "../../../../utils/transform";
import type { Vec3 } from "../types";
import {
  DatumOptionsBase,
  DatumType,
  DATUM_COLORS,
} from "./base-type";
import { buildCircleLine, buildDashedSegments } from "./internals/geometry";
import { applyVisibility, setProfileView, setModelView } from "./internals/visibility";
import { disposeSubElements } from "./internals/lifecycle";

const BUBBLE_RADIUS = 0.45;
const CLIP_DIST = BUBBLE_RADIUS + 0.1;
const ARROW_LENGTH = 0.8;
const ARROW_HEAD_SIZE = 0.25;
const LABEL_FONT_SIZE = 0.25;

export interface SectionHeadLabel {
  /** Drawing number assigned when the linked view is placed on a sheet. */
  drawingNumber: string;
  /** Sheet number carrying the linked view. */
  sheetNumber: string;
}

export interface SectionLineOptions extends DatumOptionsBase {
  datumType: DatumType.SECTION_LINE;
  /** Start of the cut line on the XZ plane. */
  start: [number, number, number];
  /** End of the cut line on the XZ plane. */
  end: [number, number, number];
  /** Near / far view-depth clipping distances from the cut line. */
  depth: { near: number; far: number };
  /** Flip the cut direction arrow. */
  flip: boolean;
  /** Additional interior vertices for stepped / jogged sections. */
  jogs?: [number, number, number][];
  /** Head labels at either end of the cut. */
  headStart: SectionHeadLabel;
  headEnd: SectionHeadLabel;
  /** Identifier of the section view this line generates, if any. */
  linkedViewId?: string;
}

/**
 * Cutting-plane datum. A dashed line terminating in two heads — a bubble
 * containing drawing/sheet references sits at each line end, with a
 * chevron arrow pointing in the view direction.
 */
export class SectionLine extends OPElement implements IShape, IPlaceable {
  ogid: string = crypto.randomUUID();
  ogType: string = ElementType.DATUM_SECTION_LINE;

  subElements2D: Map<string, THREE.Object3D> = new Map();
  subElements3D: Map<string, THREE.Object3D> = new Map();

  selected = false;
  edit = false;
  locked = false;

  onDatumUpdated: Event<null> = new Event();

  private _profileView = true;
  private _modelView = true;

  propertySet: SectionLineOptions = {
    labelName: "Section",
    datumType: DatumType.SECTION_LINE,
    color: DATUM_COLORS.SECTION,
    lineWidth: 2,
    visibility: { plan: true, model: true },
    start: [-3, 0, 0],
    end: [3, 0, 0],
    depth: { near: 0, far: 10 },
    flip: false,
    jogs: undefined,
    headStart: { drawingNumber: "?", sheetNumber: "?" },
    headEnd: { drawingNumber: "?", sheetNumber: "?" },
    linkedViewId: undefined,
  };

  constructor(config?: Partial<SectionLineOptions>) {
    super();
    if (config) {
      this.propertySet = {
        ...this.propertySet,
        ...config,
        visibility: {
          ...this.propertySet.visibility,
          ...(config.visibility ?? {}),
        },
        depth: { ...this.propertySet.depth, ...(config.depth ?? {}) },
        headStart: { ...this.propertySet.headStart, ...(config.headStart ?? {}) },
        headEnd: { ...this.propertySet.headEnd, ...(config.headEnd ?? {}) },
      };
    }
    this.propertySet.ogid = this.ogid;
    this.setOPGeometry();
    const resolved = applyVisibility(
      this.subElements2D,
      this.subElements3D,
      this.propertySet.visibility,
    );
    this._profileView = resolved.plan;
    this._modelView = resolved.model;
  }

  move(delta: Vec3): void {
    const [sx, sy, sz] = this.propertySet.start;
    const [ex, ey, ez] = this.propertySet.end;
    this.propertySet.start = [sx + delta[0], sy + delta[1], sz + delta[2]];
    this.propertySet.end = [ex + delta[0], ey + delta[1], ez + delta[2]];
    if (this.propertySet.jogs) {
      this.propertySet.jogs = this.propertySet.jogs.map(
        ([jx, jy, jz]) => [jx + delta[0], jy + delta[1], jz + delta[2]] as [number, number, number],
      );
    }
    this.setOPGeometry();
    this.onDatumUpdated.trigger(null);
  }

  rotate(angleDeg: number, axis: Vec3 = Y_UP, anchor?: Vec3): void {
    const rad = degToRad(angleDeg);
    const anc: Vec3 = anchor ?? this.propertySet.start;
    this.propertySet.start = rotatePoint(this.propertySet.start, rad, axis, anc);
    this.propertySet.end = rotatePoint(this.propertySet.end, rad, axis, anc);
    if (this.propertySet.jogs) {
      this.propertySet.jogs = this.propertySet.jogs.map(
        (j) => rotatePoint(j, rad, axis, anc) as [number, number, number],
      );
    }
    this.setOPGeometry();
    this.onDatumUpdated.trigger(null);
  }

  get labelName(): string {
    return this.propertySet.labelName;
  }
  set labelName(value: string) {
    this.propertySet.labelName = value;
    this.setOPGeometry();
  }

  get datumType(): DatumType {
    return this.propertySet.datumType;
  }

  get profileView(): boolean {
    return this._profileView;
  }
  set profileView(value: boolean) {
    this._profileView = value;
    setProfileView(this.subElements2D, value);
  }

  get modelView(): boolean {
    return this._modelView;
  }
  set modelView(value: boolean) {
    this._modelView = value;
    setModelView(this.subElements3D, value);
  }

  setOPConfig(config: Partial<SectionLineOptions>): void {
    disposeSubElements(this.subElements2D, this.subElements3D);
    this.propertySet = {
      ...this.propertySet,
      ...config,
      visibility: {
        ...this.propertySet.visibility,
        ...(config.visibility ?? {}),
      },
      depth: { ...this.propertySet.depth, ...(config.depth ?? {}) },
      headStart: { ...this.propertySet.headStart, ...(config.headStart ?? {}) },
      headEnd: { ...this.propertySet.headEnd, ...(config.headEnd ?? {}) },
    };
    this.setOPGeometry();
    this.setOPMaterial();
  }

  getOPConfig(): SectionLineOptions {
    return this.propertySet;
  }

  setOPMaterial(): void {
    // Intentionally empty — geometry rebuilds handle every visual update.
  }

  setOPGeometry(): void {
    disposeSubElements(this.subElements2D, this.subElements3D);
    const color = this.propertySet.color ?? DATUM_COLORS.SECTION;

    const polyline: [number, number, number][] = [
      this.propertySet.start,
      ...(this.propertySet.jogs ?? []),
      this.propertySet.end,
    ];
    const toVec = (p: [number, number, number]) => new Vector3(p[0], p[1], p[2]);

    // Clip both endpoints inward so dashed segments don't overlap the bubbles.
    const n = polyline.length;
    const s0 = polyline[0], s1 = polyline[1];
    const dxs = s1[0] - s0[0], dzs = s1[2] - s0[2];
    const lens = Math.sqrt(dxs * dxs + dzs * dzs) || 1;
    const clippedStart: [number, number, number] = [
      s0[0] + (dxs / lens) * CLIP_DIST,
      s0[1],
      s0[2] + (dzs / lens) * CLIP_DIST,
    ];

    const e0 = polyline[n - 1], e1 = polyline[n - 2];
    const dxe = e1[0] - e0[0], dze = e1[2] - e0[2];
    const lene = Math.sqrt(dxe * dxe + dze * dze) || 1;
    const clippedEnd: [number, number, number] = [
      e0[0] + (dxe / lene) * CLIP_DIST,
      e0[1],
      e0[2] + (dze / lene) * CLIP_DIST,
    ];

    const adjusted: [number, number, number][] = [
      clippedStart,
      ...polyline.slice(1, -1),
      clippedEnd,
    ];

    for (let i = 0; i < adjusted.length - 1; i++) {
      const seg = buildDashedSegments(toVec(adjusted[i]), toVec(adjusted[i + 1]), color, 0.6, 0.25);
      this.add(seg);
      this.subElements3D.set(`seg-${i}`, seg);
    }

    const startVec = toVec(this.propertySet.start);
    const endVec = toVec(this.propertySet.end);
    this.buildHead(startVec, endVec, this.propertySet.headStart, "headStart");
    this.buildHead(endVec, startVec, this.propertySet.headEnd, "headEnd");

    this.onDatumUpdated.trigger(null);
  }

  private buildHead(
    atPoint: Vector3,
    oppositePoint: Vector3,
    labels: SectionHeadLabel,
    keyPrefix: string,
  ) {
    const color = this.propertySet.color ?? DATUM_COLORS.SECTION;
    const width = this.propertySet.lineWidth ?? 2;

    // Unit vector along the cut from atPoint toward interior.
    const dx = oppositePoint.x - atPoint.x;
    const dz = oppositePoint.z - atPoint.z;
    const len = Math.sqrt(dx * dx + dz * dz) || 1;
    const ux = dx / len;
    const uz = dz / len;

    // Perpendicular = view direction.
    const flip = this.propertySet.flip ? -1 : 1;
    const perpX = -uz * flip;
    const perpZ = ux * flip;

    // Bubble centered at the line endpoint.
    const bubble = buildCircleLine(atPoint, BUBBLE_RADIUS, color);
    this.add(bubble);
    this.subElements3D.set(`${keyPrefix}-bubble`, bubble);

    const divider = new Line({
      start: new Vector3(atPoint.x - BUBBLE_RADIUS * 0.9, atPoint.y, atPoint.z),
      end: new Vector3(atPoint.x + BUBBLE_RADIUS * 0.9, atPoint.y, atPoint.z),
      color,
      fatLines: true,
      width: 1,
    });
    this.add(divider);
    this.subElements3D.set(`${keyPrefix}-divider`, divider);

    // Arrow: shaft from circle edge outward in the view direction, chevron head at tip.
    const shaftStart = new Vector3(
      atPoint.x + perpX * BUBBLE_RADIUS,
      atPoint.y,
      atPoint.z + perpZ * BUBBLE_RADIUS,
    );
    const arrowTip = new Vector3(
      atPoint.x + perpX * (BUBBLE_RADIUS + ARROW_LENGTH),
      atPoint.y,
      atPoint.z + perpZ * (BUBBLE_RADIUS + ARROW_LENGTH),
    );

    const shaft = new Line({ start: shaftStart, end: arrowTip, color, fatLines: true, width });
    this.add(shaft);
    this.subElements3D.set(`${keyPrefix}-arrowShaft`, shaft);

    // Chevron: two lines from tip going back at 45°.
    const sideX = -perpZ;
    const sideZ = perpX;
    const headA = new Line({
      start: arrowTip,
      end: new Vector3(
        arrowTip.x - perpX * ARROW_HEAD_SIZE + sideX * ARROW_HEAD_SIZE * 0.5,
        arrowTip.y,
        arrowTip.z - perpZ * ARROW_HEAD_SIZE + sideZ * ARROW_HEAD_SIZE * 0.5,
      ),
      color,
      fatLines: true,
      width,
    });
    const headB = new Line({
      start: arrowTip,
      end: new Vector3(
        arrowTip.x - perpX * ARROW_HEAD_SIZE - sideX * ARROW_HEAD_SIZE * 0.5,
        arrowTip.y,
        arrowTip.z - perpZ * ARROW_HEAD_SIZE - sideZ * ARROW_HEAD_SIZE * 0.5,
      ),
      color,
      fatLines: true,
      width,
    });
    this.add(headA);
    this.add(headB);
    this.subElements3D.set(`${keyPrefix}-arrowHeadA`, headA);
    this.subElements3D.set(`${keyPrefix}-arrowHeadB`, headB);

    // Glyphs: drawing number in upper half (−Z), sheet number in lower half (+Z).
    // Flat on XZ plane: local Y maps to world Z for centering.
    const drawingGlyph = Glyphs.addGlyph(labels.drawingNumber, LABEL_FONT_SIZE, color, true);
    drawingGlyph.textMesh.geometry.computeBoundingBox();
    const dbox = drawingGlyph.textMesh.geometry.boundingBox!;
    const dhw = (dbox.max.x - dbox.min.x) * 0.5;
    const dhd = (dbox.max.y - dbox.min.y) * 0.5;
    drawingGlyph.position.set(
      atPoint.x - dhw,
      atPoint.y,
      atPoint.z - BUBBLE_RADIUS * 0.3 - dhd,
    );
    this.add(drawingGlyph);
    this.subElements3D.set(`${keyPrefix}-labelDrawing`, drawingGlyph);

    const sheetGlyph = Glyphs.addGlyph(labels.sheetNumber, LABEL_FONT_SIZE, color, true);
    sheetGlyph.textMesh.geometry.computeBoundingBox();
    const sbox = sheetGlyph.textMesh.geometry.boundingBox!;
    const shw = (sbox.max.x - sbox.min.x) * 0.5;
    const shd = (sbox.max.y - sbox.min.y) * 0.5;
    sheetGlyph.position.set(
      atPoint.x - shw,
      atPoint.y,
      atPoint.z + BUBBLE_RADIUS * 0.3 - shd,
    );
    this.add(sheetGlyph);
    this.subElements3D.set(`${keyPrefix}-labelSheet`, sheetGlyph);
  }
}
